import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Mail,
  RefreshCw,
  Unplug,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import apiService from '../services/api';
import {
  CompanyEmailIntegration,
  InboundEmailLogEntry,
  WorkOrderEmailImportPreview,
} from '../types';

const WIZARD_STEPS = [
  { id: 1, title: 'Connect Gmail' },
  { id: 2, title: 'Test import' },
  { id: 3, title: 'Go live' },
];

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'Created':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'SkippedNeedsReview':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    case 'Failed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  }
};

const formatStatusLabel = (status: string) => status.replace(/([a-z])([A-Z])/g, '$1 $2');

const EmailPreviewSummary: React.FC<{ preview?: WorkOrderEmailImportPreview }> = ({ preview }) => {
  if (!preview) return null;

  const suggested = preview.suggestedCreateWorkOrder;
  const topClient = preview.clientSuggestions[0];
  const topLocation = preview.locationSuggestions[0];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{preview.parserUsed}</Badge>
        <Badge variant="outline">{preview.overallConfidence} confidence</Badge>
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <dt className="text-gray-500 dark:text-gray-400">External WO #</dt>
          <dd className="font-medium">{suggested.externalWorkOrderNumber || preview.extractedRaw.externalWorkOrderNumber || '—'}</dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-gray-400">Client match</dt>
          <dd className="font-medium">
            {topClient ? `${topClient.clientName} (${topClient.matchScore}%)` : suggested.clientId ? `Client #${suggested.clientId}` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-gray-400">Location match</dt>
          <dd className="font-medium">
            {topLocation ? `${topLocation.locationName} (${topLocation.matchScore}%)` : suggested.clientLocationId ? `Location #${suggested.clientLocationId}` : '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const EmailIntegrationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizardStep, setWizardStep] = useState(1);
  const [testEmailText, setTestEmailText] = useState('');
  const [latestTestEmail, setLatestTestEmail] = useState<InboundEmailLogEntry | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [allowlistText, setAllowlistText] = useState('');

  const { data: integrationResponse, isLoading } = useQuery({
    queryKey: ['email-integration'],
    queryFn: () => apiService.getEmailIntegration(),
  });

  const integration: CompanyEmailIntegration | undefined = integrationResponse?.data;
  const showWizard = !integration?.isConfigured;

  useEffect(() => {
    if (integration?.allowedSenderDomains?.length) {
      setAllowlistText(integration.allowedSenderDomains.join(', '));
    }
  }, [integration?.allowedSenderDomains]);

  useEffect(() => {
    if (searchParams.get('gmailConnected') === '1') {
      toast.success('Gmail connected successfully');
      queryClient.invalidateQueries({ queryKey: ['email-integration'] });
      setWizardStep(2);
      searchParams.delete('gmailConnected');
      setSearchParams(searchParams, { replace: true });
    }

    const gmailError = searchParams.get('gmailError');
    if (gmailError) {
      toast.error(`Gmail connection failed: ${gmailError}`);
      searchParams.delete('gmailError');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  useEffect(() => {
    if (integration?.isGmailConnected && wizardStep === 1 && showWizard) {
      setWizardStep(2);
    }
  }, [integration?.isGmailConnected, wizardStep, showWizard]);

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof apiService.updateEmailIntegration>[0]) =>
      apiService.updateEmailIntegration(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.setQueryData(['email-integration'], response);
        toast.success('Settings saved');
      } else {
        toast.error(response.message || 'Failed to save settings');
      }
    },
    onError: () => toast.error('Failed to save settings'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiService.disconnectGmail(),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.setQueryData(['email-integration'], response);
        setWizardStep(1);
        toast.success('Gmail disconnected');
      } else {
        toast.error(response.message || 'Failed to disconnect');
      }
    },
    onError: () => toast.error('Failed to disconnect Gmail'),
  });

  const completeSetupMutation = useMutation({
    mutationFn: () => apiService.completeEmailIntegrationSetup(),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['email-integration'] });
        toast.success('Email integration is live');
      } else {
        toast.error(response.message || 'Failed to complete setup');
      }
    },
    onError: () => toast.error('Failed to complete setup'),
  });

  const handleConnectGmail = useCallback(async () => {
    setIsConnecting(true);
    try {
      const response = await apiService.getGmailConnectUrl();
      if (response.success && response.data?.authUrl) {
        window.location.href = response.data.authUrl;
        return;
      }
      toast.error(response.message || 'Gmail connect is not available');
    } catch {
      toast.error('Failed to start Gmail connection');
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const handlePasteTest = async () => {
    if (!testEmailText.trim()) {
      toast.error('Paste a work order email to test');
      return;
    }

    try {
      const response = await apiService.testEmailIntegration(testEmailText.trim());
      if (response.success && response.data) {
        setLatestTestEmail(response.data);
        toast.success('Test email processed');
      } else {
        toast.error(response.message || 'Failed to process test email');
      }
    } catch {
      toast.error('Failed to process test email');
    }
  };

  const handleSaveAllowlist = async () => {
    const domains = allowlistText
      .split(/[,;\n]/)
      .map((value) => value.trim().replace(/^@/, '').toLowerCase())
      .filter(Boolean);

    await updateMutation.mutateAsync({ allowedSenderDomains: domains });
  };

  const { data: recentLogsResponse } = useQuery({
    queryKey: ['inbound-email-logs', 'recent'],
    queryFn: () => apiService.getInboundEmailLogs(1, 5),
    enabled: !!integration?.isConfigured,
  });

  const recentLogs = recentLogsResponse?.data?.data ?? [];

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent-color" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Button variant="outline" asChild>
            <Link to="/admin" className="flex items-center space-x-2">
              <ArrowLeft className="h-4 w-4" />
              <span>System Settings</span>
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Mail className="h-6 w-6 text-gray-900 dark:text-gray-100" />
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Email Integration</h1>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Connect the Gmail inbox that receives work order emails from your clients and vendors. Unread mail from your allowed sender domains is imported automatically.
            </p>
          </div>
        </div>
        {integration?.isConfigured && (
          <Button variant="outline" asChild>
            <Link to="/admin/email-integration/log">View full log</Link>
          </Button>
        )}
      </div>

      {showWizard ? (
        <div className="max-w-4xl space-y-6">
          <div className="flex flex-wrap gap-3">
            {WIZARD_STEPS.map((step) => (
              <div
                key={step.id}
                className={clsx(
                  'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border',
                  wizardStep === step.id
                    ? 'border-accent-color bg-accent-color/10 text-accent-color'
                    : wizardStep > step.id
                      ? 'border-green-500 text-green-700 dark:text-green-300'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500'
                )}
              >
                {wizardStep > step.id ? <CheckCircle2 className="h-4 w-4" /> : <span>{step.id}</span>}
                <span>{step.title}</span>
              </div>
            ))}
          </div>

          {wizardStep === 1 && (
            <div className="card">
              <div className="card-header">Step 1 — Connect Gmail</div>
              <div className="card-body space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Sign in with the Google account that receives work order emails (e.g. your dispatch inbox).
                  WorkOrder HQ only reads unread mail from the sender domains you allow below.
                </p>

                <div>
                  <label className="block text-sm font-medium mb-2">Allowed sender domains</label>
                  <input
                    className="input"
                    placeholder="e.g. vendor.com, client-cmms.com"
                    value={allowlistText}
                    onChange={(event) => setAllowlistText(event.target.value)}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Only emails from these domains are imported. Leave blank to import from any sender.
                  </p>
                </div>

                {!integration?.gmailOAuthConfigured && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-900 dark:text-amber-100">
                    Gmail OAuth is not configured on the server yet. Ask your administrator to set Google Cloud credentials, or use paste-to-test in Step 2 if available.
                  </div>
                )}

                {integration?.isGmailConnected ? (
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>Connected: <strong>{integration.connectedEmail}</strong></span>
                  </div>
                ) : (
                  <Button type="button" onClick={handleConnectGmail} disabled={isConnecting || !integration?.gmailOAuthConfigured}>
                    {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Connect Gmail
                  </Button>
                )}

                <div className="flex justify-end gap-3">
                  {integration?.isGmailConnected && (
                    <Button type="button" variant="outline" onClick={handleSaveAllowlist} disabled={updateMutation.isPending}>
                      Save sender domains
                    </Button>
                  )}
                  <Button type="button" onClick={() => setWizardStep(2)} disabled={!integration?.isGmailConnected}>
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="card">
              <div className="card-header">Step 2 — Test import</div>
              <div className="card-body space-y-6">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Paste a work order email below to verify parsing, or leave a matching email unread in your connected inbox — we check every few minutes.
                </p>

                <textarea
                  className="input min-h-[160px] font-mono text-xs"
                  placeholder="Paste a work order email here..."
                  value={testEmailText}
                  onChange={(event) => setTestEmailText(event.target.value)}
                />
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={handlePasteTest}>
                    Process pasted email
                  </Button>
                </div>

                {latestTestEmail && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Email parsed successfully</span>
                    </div>
                    <EmailPreviewSummary preview={latestTestEmail.preview} />
                  </div>
                )}

                <div className="flex justify-between gap-3">
                  <Button type="button" variant="outline" onClick={() => setWizardStep(1)}>Back</Button>
                  <Button type="button" onClick={() => setWizardStep(3)} disabled={!latestTestEmail}>
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}

          {wizardStep === 3 && (
            <div className="card">
              <div className="card-header">Step 3 — Go live</div>
              <div className="card-body space-y-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-gray-300"
                    checked={integration?.autoCreateEnabled ?? false}
                    onChange={(event) => updateMutation.mutate({ autoCreateEnabled: event.target.checked })}
                  />
                  <span>
                    <span className="block font-medium">Automatically create work orders</span>
                    <span className="block text-sm text-gray-500 dark:text-gray-400 mt-1">
                      When off, emails are logged for review. Turn on once you trust the matches from your test.
                    </span>
                  </span>
                </label>

                <div className="flex justify-between gap-3">
                  <Button type="button" variant="outline" onClick={() => setWizardStep(2)}>Back</Button>
                  <Button type="button" onClick={() => completeSetupMutation.mutate()} disabled={completeSetupMutation.isPending}>
                    {completeSetupMutation.isPending ? 'Finishing...' : 'Finish setup'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="max-w-5xl space-y-6">
          <div className="card">
            <div className="card-body flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-semibold">Gmail connected: {integration?.connectedEmail}</span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Importing unread mail from:{' '}
                  {integration?.allowedSenderDomains.length
                    ? integration.allowedSenderDomains.map((d) => `@${d}`).join(', ')
                    : 'all senders (no domain filter)'}
                  {integration?.lastEmailReceivedAt && ` · Last email ${new Date(integration.lastEmailReceivedAt).toLocaleString()}`}
                  {integration?.lastPollError && (
                    <span className="text-red-600 dark:text-red-400"> · Poll error: {integration.lastPollError}</span>
                  )}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => disconnectMutation.mutate()}
                disabled={disconnectMutation.isPending}
              >
                <Unplug className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">Automation</div>
            <div className="card-body space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-gray-300"
                  checked={integration?.autoCreateEnabled ?? false}
                  onChange={(event) => updateMutation.mutate({ autoCreateEnabled: event.target.checked })}
                  disabled={updateMutation.isPending}
                />
                <span>
                  <span className="block font-medium">Automatically create work orders from email</span>
                  <span className="block text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Requires high-confidence client and location matches.
                  </span>
                </span>
              </label>

              <div>
                <label className="block text-sm font-medium mb-2">Allowed sender domains</label>
                <input
                  className="input"
                  placeholder="e.g. vendor.com, client-cmms.com"
                  value={allowlistText}
                  onChange={(event) => setAllowlistText(event.target.value)}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Only unread emails from these domains are imported.</p>
                <div className="mt-3 flex justify-end">
                  <Button type="button" variant="outline" onClick={handleSaveAllowlist} disabled={updateMutation.isPending}>
                    Save senders
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header flex items-center justify-between">
              <span>Recent inbound emails</span>
              <Link to="/admin/email-integration/log" className="text-sm text-accent-color hover:underline">View all</Link>
            </div>
            <div className="card-body overflow-x-auto">
              {recentLogs.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No emails processed yet. Leave a work order email unread in your inbox, or use paste-to-test during setup.</p>
              ) : (
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-2 pr-4 font-medium">Received</th>
                      <th className="pb-2 pr-4 font-medium">Subject</th>
                      <th className="pb-2 pr-4 font-medium">From</th>
                      <th className="pb-2 pr-4 font-medium">Status</th>
                      <th className="pb-2 pr-4 font-medium">Work order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogs.map((entry) => (
                      <tr key={entry.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-3 pr-4 text-sm">{new Date(entry.receivedAt).toLocaleString()}</td>
                        <td className="py-3 pr-4 text-sm max-w-xs truncate">{entry.subject || '—'}</td>
                        <td className="py-3 pr-4 text-sm max-w-xs truncate">{entry.fromAddress || '—'}</td>
                        <td className="py-3 pr-4">
                          <span className={clsx('inline-flex px-2 py-1 rounded-full text-xs font-medium', statusBadgeClass(entry.status))}>
                            {formatStatusLabel(entry.status)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-sm">
                          {entry.workOrderId ? (
                            <Link to={`/workorders/${entry.workOrderId}/edit`} className="text-accent-color hover:underline">
                              {entry.workOrderNumber || `WO #${entry.workOrderId}`}
                            </Link>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailIntegrationPage;
